import { Navigate, type RouteObject } from 'react-router';

import {
  AuditAdminLayout,
  MasterDataLayout,
  DirectoryAdminLayout,
  AiAdminLayout,
  MailAdminLayout,
  ProductsAdminLayout,
  RequireAuthLayout,
  RolesAdminLayout,
  TenantsAdminLayout,
  UsersAdminLayout,
  NotificationStandardAdminLayout,
  TypographyAdminLayout,
  VoiceAdminLayout,
} from '@/platform/auth/require-permission';
import { AdminLayout } from '@/platform/layouts/admin-layout';
import { PlatformShellLayout } from '@/platform/layouts/platform-shell';
import { LegacyAdminRedirect } from '@/platform/legacy-admin-redirect';
import {
  ADMIN_ROOT,
  AI_ADMIN_PATH,
  AUDIT_ADMIN_PATH,
  DEPARTMENTS_ADMIN_PATH,
  DIRECTORY_ADMIN_PATH,
  MAIL_ADMIN_PATH,
  SITES_ADMIN_PATH,
  MODULES_ADMIN_PATH,
  PRODUCTS_ADMIN_LEGACY_PATH,
  ROLES_ADMIN_PATH,
  TEAMS_ADMIN_PATH,
  TENANTS_ADMIN_PATH,
  USERS_ADMIN_PATH,
  NOTIFICATION_STANDARD_ADMIN_PATH,
  TYPOGRAPHY_ADMIN_PATH,
  VOICE_ADMIN_PATH,
} from '@/platform/lib/admin';
import { NOTIFICATIONS_PATH, PROFILE_PATH } from '@/platform/lib/paths';
import { START_PATH } from '@/platform/lib/products';
import { MASTER_DATA_ROOT } from '@/platform/lib/master-data-paths';
import { ArticleDetailPage } from '@/platform/pages/articles/article-detail-page';
import { ArticleFormPage } from '@/platform/pages/articles/article-form-page';
import { ArticleGroupsPage } from '@/platform/pages/articles/article-groups-page';
import { ArticlesPage } from '@/platform/pages/articles/articles-page';
import { CostCentersPage } from '@/platform/pages/master-data/cost-centers-page';
import { CurrenciesPage } from '@/platform/pages/master-data/currencies-page';
import { MasterDataExchangePage } from '@/platform/pages/master-data/master-data-exchange-page';
import {
  MasterDataIndexRedirect,
  MasterDataLayoutPage,
} from '@/platform/pages/master-data/master-data-layout';
import { PartnerDetailPage } from '@/platform/pages/master-data/partner-detail-page';
import { PartnerFormPage } from '@/platform/pages/master-data/partner-form-page';
import { PartnersPage } from '@/platform/pages/master-data/partners-page';
import { PaymentTermsPage } from '@/platform/pages/master-data/payment-terms-page';
import { UnitsPage } from '@/platform/pages/master-data/units-page';
import { AdminHomePage } from '@/platform/pages/admin-home-page';
import { AiAdminPage } from '@/platform/pages/ai-admin-page';
import { AuditPage } from '@/platform/pages/audit-page';
import { DepartmentsPage } from '@/platform/pages/departments-page';
import { SitesPage } from '@/platform/pages/sites-page';
import { DirectoryAdminPage } from '@/platform/pages/directory-admin-page';
import { MailAdminPage } from '@/platform/pages/mail-admin-page';
import { MitarbeiterportalHomePage } from '@/platform/pages/mitarbeiterportal-home-page';
import { NotificationsPage } from '@/platform/pages/notifications-page';
import { ModulesAdminPage } from '@/platform/pages/modules-admin-page';
import { ProfilePage } from '@/platform/pages/profile-page';
import { ProfileAccountPage } from '@/platform/pages/profile/profile-account-page';
import { ProfileAdminPage } from '@/platform/pages/profile/profile-admin-page';
import { ProfileAppearancePage } from '@/platform/pages/profile/profile-appearance-page';
import { ProfileDevicesPage } from '@/platform/pages/profile/profile-devices-page';
import { ProfileModulePage } from '@/platform/pages/profile/profile-module-page';
import { ProfileNotificationsPage } from '@/platform/pages/profile/profile-notifications-page';
import { ProfileOverviewPage } from '@/platform/pages/profile/profile-overview-page';
import { ProfileSecurityPage } from '@/platform/pages/profile/profile-security-page';
import { RolesPage } from '@/platform/pages/roles-page';
import { TeamsPage } from '@/platform/pages/teams-page';
import { TenantsAdminPage } from '@/platform/pages/tenants-admin-page';
import { NotificationStandardAdminPage } from '@/platform/pages/notification-standard-admin-page';
import { TypographyAdminPage } from '@/platform/pages/typography-admin-page';
import { VoiceAdminPage } from '@/platform/pages/voice-admin-page';
import { UserDetailPage } from '@/platform/pages/user-detail-page';
import { UsersPage } from '@/platform/pages/users-page';

/** Mitarbeiterportal Hauptmenü and central (platform-owned) administration - no Helpdesk chrome. */
export const platformRoute: RouteObject = {
  Component: RequireAuthLayout,
  children: [
    {
      Component: PlatformShellLayout,
      children: [
        { path: START_PATH, Component: MitarbeiterportalHomePage },
        {
          Component: MasterDataLayout,
          children: [
            {
              path: MASTER_DATA_ROOT,
              Component: MasterDataLayoutPage,
              children: [
                {
                  index: true,
                  element: <MasterDataIndexRedirect />,
                },
                { path: 'partner', Component: PartnersPage },
                { path: 'partner/neu', Component: PartnerFormPage },
                { path: 'partner/:partnerId', Component: PartnerDetailPage },
                { path: 'artikel', Component: ArticlesPage },
                { path: 'artikel/neu', Component: ArticleFormPage },
                { path: 'artikel/gruppen', Component: ArticleGroupsPage },
                { path: 'artikel/:articleId', Component: ArticleDetailPage },
                { path: 'zahlungen', Component: PaymentTermsPage },
                { path: 'waehrungen', Component: CurrenciesPage },
                { path: 'einheiten', Component: UnitsPage },
                { path: 'kostenstellen', Component: CostCentersPage },
                { path: 'austausch', Component: MasterDataExchangePage },
              ],
            },
          ],
        },
        {
          path: PROFILE_PATH,
          Component: ProfilePage,
          children: [
            { index: true, Component: ProfileOverviewPage },
            { path: 'konto', Component: ProfileAccountPage },
            { path: 'persoenlich', element: <Navigate to={PROFILE_PATH} replace /> },
            { path: 'darstellung', Component: ProfileAppearancePage },
            { path: 'mitteilungen', Component: ProfileNotificationsPage },
            { path: 'geraete', Component: ProfileDevicesPage },
            { path: 'sicherheit', Component: ProfileSecurityPage },
            { path: 'verwaltung', Component: ProfileAdminPage },
            { path: ':moduleSegment', Component: ProfileModulePage },
          ],
        },
        { path: NOTIFICATIONS_PATH, Component: NotificationsPage },
        {
          path: ADMIN_ROOT,
          Component: AdminLayout,
          children: [
            { index: true, Component: AdminHomePage },
            {
              Component: UsersAdminLayout,
              children: [
                { path: USERS_ADMIN_PATH, Component: UsersPage },
                { path: `${USERS_ADMIN_PATH}/:userId`, Component: UserDetailPage },
                { path: DEPARTMENTS_ADMIN_PATH, Component: DepartmentsPage },
                { path: SITES_ADMIN_PATH, Component: SitesPage },
                { path: TEAMS_ADMIN_PATH, Component: TeamsPage },
              ],
            },
            {
              Component: ProductsAdminLayout,
              children: [
                { path: MODULES_ADMIN_PATH, Component: ModulesAdminPage },
                {
                  path: PRODUCTS_ADMIN_LEGACY_PATH,
                  element: <Navigate to={MODULES_ADMIN_PATH} replace />,
                },
              ],
            },
            {
              Component: TenantsAdminLayout,
              children: [{ path: TENANTS_ADMIN_PATH, Component: TenantsAdminPage }],
            },
            {
              Component: VoiceAdminLayout,
              children: [{ path: VOICE_ADMIN_PATH, Component: VoiceAdminPage }],
            },
            {
              Component: TypographyAdminLayout,
              children: [{ path: TYPOGRAPHY_ADMIN_PATH, Component: TypographyAdminPage }],
            },
            {
              Component: NotificationStandardAdminLayout,
              children: [
                {
                  path: NOTIFICATION_STANDARD_ADMIN_PATH,
                  Component: NotificationStandardAdminPage,
                },
              ],
            },
            {
              Component: AiAdminLayout,
              children: [{ path: AI_ADMIN_PATH, Component: AiAdminPage }],
            },
            {
              Component: MailAdminLayout,
              children: [{ path: MAIL_ADMIN_PATH, Component: MailAdminPage }],
            },
            {
              Component: RolesAdminLayout,
              children: [{ path: ROLES_ADMIN_PATH, Component: RolesPage }],
            },
            {
              Component: DirectoryAdminLayout,
              children: [{ path: DIRECTORY_ADMIN_PATH, Component: DirectoryAdminPage }],
            },
            {
              Component: AuditAdminLayout,
              children: [{ path: AUDIT_ADMIN_PATH, Component: AuditPage }],
            },
          ],
        },
      ],
    },
  ],
};

export const platformLegacyRedirectRoutes: RouteObject[] = [
  '/benutzer',
  '/benutzer/:userId',
  '/abteilungen',
  '/teams',
  '/berechtigungsgruppen',
  '/audit',
].map((path) => ({ path, Component: LegacyAdminRedirect }));
